import { useEffect, useRef, useState, type ReactNode } from 'react';
import type { Tri } from '../lib/tri';
import { Infobulle } from './Infobulle';

// L'infobulle vit dans son propre fichier ; on la re-exporte d'ici pour
// que les ecrans la trouvent a cote de Champ, Badge et Modale.
export { Infobulle } from './Infobulle';

export function Chargement({ libelle = 'Chargement…' }: { libelle?: string }) {
  return (
    <div className="flex items-center gap-3 p-8 text-sm text-encre-500">
      <span
        className="h-4 w-4 animate-spin rounded-full border-2 border-encre-300 border-t-encre-700"
        aria-hidden
      />
      {libelle}
    </div>
  );
}

export function Erreur({ message, onReessayer }: { message: string; onReessayer?: () => void }) {
  return (
    <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-800">
      <p>{message}</p>
      {onReessayer && (
        <button type="button" className="mt-2 underline" onClick={onReessayer}>
          Reessayer
        </button>
      )}
    </div>
  );
}

export function Vide({ message }: { message: string }) {
  return <p className="p-8 text-center text-sm text-encre-400">{message}</p>;
}

export function Badge({
  children,
  ton = 'neutre',
  classe = '',
}: {
  children: ReactNode;
  ton?: 'neutre' | 'succes' | 'alerte' | 'danger' | 'contour' | 'succesContour' | 'neutreContour';
  /** Classes en plus — une largeur fixe, pour aligner deux pastilles de texte inegal. */
  classe?: string;
}) {
  const tons = {
    neutre: 'bg-encre-100 text-encre-700',
    succes: 'bg-emerald-100 text-emerald-800',
    alerte: 'bg-amber-100 text-amber-800',
    danger: 'bg-red-100 text-red-800',
    // Fond blanc, liseret bleu : une mention a voir sans qu'elle alarme.
    // L'ambre et le rouge disent « probleme a traiter » ; une situation
    // de handicap n'en est pas un.
    // En ANNEAU interieur, pas en bordure : une bordure ajoute un pixel
    // de chaque cote, et la pastille en creux depassait la pleine.
    contour: 'bg-white text-blue-700 ring-1 ring-inset ring-blue-400',
    // Le CDD, en creux face au CDI plein (2026-10-02).
    succesContour: 'bg-white text-emerald-700 ring-1 ring-inset ring-emerald-500',
    // Un compte, en creux et sans couleur : le nombre de creneaux d'une activite.
    neutreContour: 'bg-white text-encre-700 ring-1 ring-inset ring-encre-300',
  } as const;

  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${tons[ton]} ${classe}`}
    >
      {children}
    </span>
  );
}

/**
 * Un « ? » entoure qui deplie un texte d'aide au survol ou au focus. Le
 * texte long qui vivait sous le titre de la page y passe (2026-10-02) :
 * on le lit une fois, il n'a pas a occuper l'ecran tous les jours.
 */
/** Le bouton « ? » d'un ecran : l'explication longue, en dessous, par l'infobulle commune. */
export function AideEnBulle({ texte }: { texte: string }) {
  return (
    <Infobulle texte={texte} position="bas" large className="inline-flex">
      <button
        type="button"
        className="flex h-5 w-5 items-center justify-center rounded-full border border-encre-300 text-xs font-medium text-encre-500 hover:border-encre-500 hover:text-encre-800"
        aria-label={texte}
      >
        ?
      </button>
    </Infobulle>
  );
}

export function EnTetePage({
  titre,
  description,
  actions,
}: {
  /** ReactNode et non `string` : un titre peut porter une pastille de statut. */
  titre: ReactNode;
  description?: string;
  actions?: ReactNode;
}) {
  return (
    <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
      <div>
        <h1 className="flex flex-wrap items-center gap-3 text-2xl font-semibold text-encre-900">
          {titre}
        </h1>
        {description && <p className="mt-1 max-w-2xl text-sm text-encre-500">{description}</p>}
      </div>
      {actions && <div className="flex gap-2">{actions}</div>}
    </div>
  );
}

/**
 * Sous-navigation d'une section. « Adherents » recouvre deux vues — les
 * familles (l'entite payeuse) et les personnes (les usagers) — sans
 * meriter deux entrees dans le bandeau principal.
 */
export function Onglets({
  onglets,
  actif,
  onChanger,
}: {
  onglets: { cle: string; libelle: string }[];
  actif: string;
  onChanger: (cle: string) => void;
}) {
  return (
    <div className="mb-6 flex gap-1 border-b border-encre-200">
      {onglets.map((onglet) => (
        <button
          key={onglet.cle}
          type="button"
          onClick={() => onChanger(onglet.cle)}
          className={`-mb-px border-b-2 px-4 py-2 text-sm transition ${
            onglet.cle === actif
              ? 'border-encre-900 font-medium text-encre-900'
              : 'border-transparent text-encre-500 hover:text-encre-800'
          }`}
        >
          {onglet.libelle}
        </button>
      ))}
    </div>
  );
}

/** Le champ (ou un de ses parents) est-il marque `data-navigation` ? */
function estNavigation(cible: EventTarget | null): boolean {
  return cible instanceof Element && cible.closest('[data-navigation]') !== null;
}

export function Modale({
  titre,
  titreAccessible,
  ouverte,
  onFermer,
  children,
  large = false,
  tresLarge = false,
  actions,
}: {
  /** Texte, ou fragment quand le titre porte une pastille de statut. */
  titre: ReactNode;
  /**
   * Le titre lu par un lecteur d'ecran. Obligatoire des que `titre` n'est
   * pas du texte : `aria-label` ne prend qu'une chaine, et un fragment y
   * deviendrait « [object Object] ».
   */
  titreAccessible?: string;
  ouverte: boolean;
  onFermer: () => void;
  children: ReactNode;
  /** Panneau elargi, pour un contenu en tableau (detail de paie). */
  large?: boolean;
  /** Plus large encore, pour deux colonnes dont l'une porte un tableau (acceptation d'une demande). */
  tresLarge?: boolean;
  /**
   * Actions posees a GAUCHE de la croix — un menu, un bouton.
   *
   * C'est une FONCTION, et elle recoit la fermeture GARDEE : quitter la
   * modale par une action doit demander la meme confirmation que la
   * quitter par la croix. Lui passer un simple `onFermer` ferait perdre
   * une saisie en cours sans un mot.
   */
  actions?: (fermer: () => void) => ReactNode;
}) {
  const modifie = useRef(false);

  // Remise a zero a chaque ouverture : la modale n'est pas demontee entre
  // deux editions, un « modifie » resterait sinon acquis a vie.
  useEffect(() => {
    if (ouverte) modifie.current = false;
  }, [ouverte]);

  /**
   * Sortie par Echap ou par clic exterieur. Tant que rien n'a ete saisi,
   * elle est immediate ; sinon elle demande confirmation, car fermer
   * perdrait la saisie.
   */
  const demanderFermeture = (): void => {
    if (
      !modifie.current ||
      window.confirm('Vos modifications ne seront pas enregistrées. Fermer quand même ?')
    ) {
      onFermer();
    }
  };

  // L'ecouteur clavier n'est pose qu'a l'ouverture ; sans cette reference
  // il appellerait la version de `onFermer` figee a ce moment-la, et
  // refermerait sur un etat perime.
  const fermetureCourante = useRef(demanderFermeture);
  fermetureCourante.current = demanderFermeture;

  useEffect(() => {
    if (!ouverte) return;
    const auClavier = (evenement: KeyboardEvent) => {
      if (evenement.key === 'Escape') fermetureCourante.current();
    };
    document.addEventListener('keydown', auClavier);
    return () => document.removeEventListener('keydown', auClavier);
  }, [ouverte]);

  if (!ouverte) return null;

  return (
    <div
      // Le CORPS de la modale est son propre conteneur de defilement
      // (2026-10-02) : le panneau tient dans la fenetre, son titre reste
      // en vue et l'ascenseur est le sien, pas celui de la page. Un
      // en-tete de tableau fige a l'interieur s'accroche donc au corps.
      // Le decalage du bandeau n'a pas de sens ici — il ferait flotter
      // l'en-tete 63 px sous le bord. Les variables CSS s'heritant, la
      // redefinir ici suffit pour tous les tableaux qu'elle contient.
      style={{ ['--hauteur-bandeau' as string]: '0px' }}
      className="fixed inset-0 z-50 flex items-start justify-center overflow-hidden bg-encre-900/40 p-4 sm:p-8"
      role="dialog"
      aria-modal="true"
      aria-label={titreAccessible ?? (typeof titre === 'string' ? titre : undefined)}
      // Seul un clic sur le fond ferme : sans cette egalite, un clic
      // relache au-dessus du panneau fermerait aussi.
      onMouseDown={(evenement) => {
        if (evenement.target === evenement.currentTarget) demanderFermeture();
      }}
    >
      {/* Un evenement `input` ou `change` natif ne nait que d'une action
          humaine : React ne l'emet pas en reaffichant un champ controle.
          C'est ce qui distingue une saisie d'une valeur qui s'installe
          seule — un <select> dont les options viennent d'arriver du
          reseau. Comparer les valeurs avant/apres aurait pris cette
          initialisation tardive pour une modification, et reclame de
          confirmer l'abandon d'un formulaire auquel personne n'a
          touche. */}
      {/* UN CHAMP DE NAVIGATION N'EST PAS UNE SAISIE. Le selecteur de mois
          du detail de paie change ce qu'on REGARDE, pas ce qu'on
          enregistre : le marquer modifie faisait demander « Fermer quand
          meme ? » a qui n'avait rien saisi (constate le 2026-09-08). Un
          tel champ porte `data-navigation`, et la modale l'ignore. */}
      <div
        // `max-h-full` borne le panneau a la fenetre (moins la marge du
        // fond) ; en colonne, l'en-tete garde sa hauteur et le corps prend
        // le reste, avec `min-h-0` sans quoi un enfant flex refuse de
        // descendre sous la taille de son contenu et rien ne defile.
        className={`carte flex max-h-full w-full flex-col ${tresLarge ? 'max-w-6xl' : large ? 'max-w-4xl' : 'max-w-lg'}`}
        onInput={(evenement) => {
          if (!estNavigation(evenement.target)) modifie.current = true;
        }}
        onChange={(evenement) => {
          if (!estNavigation(evenement.target)) modifie.current = true;
        }}
      >
        <div className="flex shrink-0 items-center justify-between border-b border-encre-200 px-5 py-4">
          <h2 className="text-lg font-semibold">{titre}</h2>
          <div className="flex items-center gap-1">
            {actions?.(demanderFermeture)}
          <button
            type="button"
            onClick={demanderFermeture}
            className="rounded p-1 text-encre-400 hover:bg-encre-100 hover:text-encre-700"
            aria-label="Fermer"
          >
            ✕
          </button>
          </div>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>
      </div>
    </div>
  );
}

export function Champ({
  libelle,
  children,
  aide,
  infobulle,
}: {
  libelle: string;
  children: ReactNode;
  /** Sous le champ, toujours visible. */
  aide?: string;
  /** Au survol du libelle seulement (2026-10-30) : pour une explication qu'on ne relit pas a chaque saisie. */
  infobulle?: string;
}) {
  return (
    <label className="block">
      <Infobulle texte={infobulle} className="block">
        <span className={`etiquette${infobulle ? ' cursor-help' : ''}`}>{libelle}</span>
      </Infobulle>
      {children}
      {aide && <span className="mt-1 block text-xs text-encre-400">{aide}</span>}
    </label>
  );
}

export interface Colonne<T> {
  cle: string;
  entete: string;
  rendu: (ligne: T) => ReactNode;
  classe?: string;
  /**
   * La valeur sur laquelle la colonne se TRIE (2026-09-29). Presente, elle
   * rend l'en-tete cliquable. Un nombre se compare en nombre, un texte en
   * francais sans tenir compte de la casse ; `null` va en fin de liste.
   */
  valeurTri?: (ligne: T) => string | number | null | undefined;
}

export function Tableau<T>({
  colonnes,
  lignes,
  cleDe,
  messageVide = 'Aucun element.',
  onLigneCliquee,
  ligneCliquable,
  tri,
  onTrier,
  onReordonner,
}: {
  colonnes: Colonne<T>[];
  lignes: T[];
  cleDe: (ligne: T) => string;
  messageVide?: string;
  onLigneCliquee?: (ligne: T) => void;
  /**
   * REORDONNER EN GLISSANT (2026-10-12). Present, chaque ligne gagne une
   * poignee et se deplace a la souris ; au depot, l'ecran recoit les cles
   * dans le nouvel ordre et decide quoi en faire. Le tableau ne reordonne
   * rien lui-meme : c'est l'ecran qui tient les lignes.
   */
  onReordonner?: (cles: string[]) => void;
  /**
   * Tri CONTROLE : l'ecran tient l'etat et passe des lignes deja triees
   * (`trierLignes`). Le tableau ne fait que dessiner l'indicateur et
   * demander le changement au clic sur un en-tete triable.
   */
  tri?: Tri | null;
  onTrier?: (tri: Tri) => void;
  /**
   * Quelles lignes repondent au clic. A defaut, toutes.
   *
   * Sans ce filtre, une ligne verrouillee prend le curseur et le survol
   * des autres, puis ne fait rien : le geste passe pour une panne. Mieux
   * vaut qu'elle n'invite pas au clic.
   */
  ligneCliquable?: (ligne: T) => boolean;
}) {
  // Le glisser-deposer : la cle de la ligne saisie, et celle survolee.
  // Les crochets vivent AVANT le retour anticipe, sans quoi React les
  // compterait differemment d'un rendu a l'autre.
  const [glissee, setGlissee] = useState<string | null>(null);
  const [survolee, setSurvolee] = useState<string | null>(null);
  const finirGlisser = () => {
    setGlissee(null);
    setSurvolee(null);
  };
  const deposer = (cible: string) => {
    if (onReordonner && glissee && glissee !== cible) {
      const cles = lignes.map(cleDe);
      const de = cles.indexOf(glissee);
      const vers = cles.indexOf(cible);
      if (de >= 0 && vers >= 0) {
        cles.splice(de, 1);
        cles.splice(vers, 0, glissee);
        onReordonner(cles);
      }
    }
    finirGlisser();
  };

  if (lignes.length === 0) return <Vide message={messageVide} />;

  return (
    // `overflow-x-auto` cree un conteneur de defilement sur LES DEUX axes
    // — mettre `overflow-x` a `auto` force `overflow-y` a `auto` aussi —,
    // et un en-tete `sticky` s'accroche alors a CE conteneur, qui ne
    // defile pas verticalement : rien ne se figerait. On ne le garde donc
    // que sous `lg`, ou l'ecran est trop etroit pour la table de toute
    // facon et ou figer l'en-tete apporte peu.
    <div className="overflow-x-auto lg:overflow-x-visible">
      <table className="min-w-full divide-y divide-encre-200 text-sm">
        <thead>
          <tr>
            {onReordonner && (
              <th
                scope="col"
                aria-label="Ordre"
                className="sticky z-10 w-8 bg-encre-50 shadow-[inset_0_-1px_0_#c5c7cc]"
                style={{ top: 'var(--hauteur-bandeau, 63px)' }}
              />
            )}
            {colonnes.map((colonne) => (
              <th
                key={colonne.cle}
                scope="col"
                // Le fond est porte par la CELLULE et non par `thead` :
                // figee, elle passe au-dessus des lignes, et une cellule
                // transparente les laisserait paraitre au travers.
                // LE LISERE EST UNE OMBRE, PAS UNE BORDURE.
                // La table est en `border-collapse: collapse` : une
                // bordure y appartient a la grille du TABLEAU, pas a la
                // cellule, et reste donc en arriere quand la cellule se
                // fige et se decale. Une ombre interieure, elle, se peint
                // avec la cellule et la suit.
                // L'OMBRE PORTEE EST UN DEGRADE, PAS UN `box-shadow`.
                // Chaque cellule projetterait la sienne, et un etalement
                // negatif — indispensable pour qu'elle ne deborde pas sur
                // les cotes — la ferait s'arreter avant le bord : on
                // verrait des pointillés aux jointures de colonnes. Un
                // pseudo-element en `inset-x-0` n'a aucun debord lateral,
                // donc les ombres voisines s'abutent sans couture.
                // `z-10`, PAS `z-20` : l'en-tete n'a qu'a dominer les
                // cellules du tableau. A `z-20`, il etait a egalite avec
                // les panneaux des filtres (ChoixMultiple, ChoixActivite),
                // et l'ordre du document tranchait en sa faveur — le
                // tableau venant apres la barre de filtres, les listes
                // deroulantes passaient SOUS ses titres de colonnes
                // (constate sur Adherents, 2026-09-08).
                className={`sticky z-10 bg-encre-50 px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-encre-500 shadow-[inset_0_-1px_0_#c5c7cc] after:pointer-events-none after:absolute after:inset-x-0 after:top-full after:h-2 after:bg-gradient-to-b after:from-black/10 after:to-transparent after:content-[''] ${colonne.classe ?? ''}`}
                style={{ top: 'var(--hauteur-bandeau, 63px)' }}
                aria-sort={
                  tri?.cle === colonne.cle
                    ? tri.sens === 'asc'
                      ? 'ascending'
                      : 'descending'
                    : undefined
                }
              >
                {colonne.valeurTri && onTrier ? (
                  // Un bouton, pour le clavier. Meme typographie que
                  // l'en-tete (un bouton perd `uppercase` par defaut).
                  <button
                    type="button"
                    className={`inline-flex items-center gap-1 uppercase tracking-wide hover:text-encre-800 ${
                      tri?.cle === colonne.cle ? 'text-encre-800' : ''
                    }`}
                    onClick={() =>
                      onTrier({
                        cle: colonne.cle,
                        // Recliquer la colonne active inverse le sens ;
                        // une autre colonne repart en croissant.
                        sens: tri?.cle === colonne.cle && tri.sens === 'asc' ? 'desc' : 'asc',
                      })
                    }
                  >
                    {colonne.entete}
                    <span aria-hidden className="text-[9px]">
                      {tri?.cle === colonne.cle ? (tri.sens === 'asc' ? '▲' : '▼') : '△'}
                    </span>
                  </button>
                ) : (
                  colonne.entete
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-encre-100 bg-white">
          {lignes.map((ligne) => (
            <tr
              key={cleDe(ligne)}
              className={[
                onLigneCliquee && (!ligneCliquable || ligneCliquable(ligne))
                  ? 'cursor-pointer hover:bg-encre-50'
                  : '',
                glissee === cleDe(ligne) ? 'opacity-40' : '',
                // La ligne survolee pendant un glisser dit ou la saisie se posera.
                survolee === cleDe(ligne) && glissee !== cleDe(ligne)
                  ? 'shadow-[inset_0_2px_0_#3b82f6]'
                  : '',
              ]
                .filter(Boolean)
                .join(' ') || undefined}
              onClick={
                onLigneCliquee && (!ligneCliquable || ligneCliquable(ligne))
                  ? () => onLigneCliquee(ligne)
                  : undefined
              }
              draggable={Boolean(onReordonner)}
              onDragStart={
                onReordonner
                  ? (evenement) => {
                      evenement.dataTransfer.effectAllowed = 'move';
                      // Firefox exige une donnee pour demarrer le glisser.
                      evenement.dataTransfer.setData('text/plain', cleDe(ligne));
                      setGlissee(cleDe(ligne));
                    }
                  : undefined
              }
              onDragOver={
                onReordonner
                  ? (evenement) => {
                      evenement.preventDefault();
                      evenement.dataTransfer.dropEffect = 'move';
                      if (survolee !== cleDe(ligne)) setSurvolee(cleDe(ligne));
                    }
                  : undefined
              }
              onDrop={
                onReordonner
                  ? (evenement) => {
                      evenement.preventDefault();
                      deposer(cleDe(ligne));
                    }
                  : undefined
              }
              onDragEnd={onReordonner ? finirGlisser : undefined}
            >
              {onReordonner && (
                <td
                  className="w-8 cursor-grab select-none px-2 py-3 text-center text-encre-300 active:cursor-grabbing"
                  // La poignee n'ouvre pas la ligne.
                  onClick={(evenement) => evenement.stopPropagation()}
                >
                  <Infobulle texte="Glisser pour changer l’ordre">
                    <span aria-hidden>⋮⋮</span>
                  </Infobulle>
                </td>
              )}
              {colonnes.map((colonne) => (
                <td key={colonne.cle} className={`px-4 py-3 ${colonne.classe ?? ''}`}>
                  {colonne.rendu(ligne)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Crayon « modifier », commun a l'ecran Calendrier et au choix d'un
 * creneau. Une seule definition : deux copies du trace divergeraient au
 * premier ajustement, et le geste doit se reconnaitre d'un ecran a
 * l'autre.
 */
export function IconeCrayon({ classe = 'h-3.5 w-3.5' }: { classe?: string }) {
  return (
    <svg viewBox="0 0 16 16" className={classe} fill="currentColor" aria-hidden>
      <path d="M11.5 1.5a1.5 1.5 0 0 1 2.12 2.12l-.94.94-2.12-2.12.94-.94ZM9.85 3.15l2.12 2.12L5.3 11.94a1 1 0 0 1-.45.26l-2.6.74.74-2.6a1 1 0 0 1 .26-.45l6.6-6.6Z" />
    </svg>
  );
}
